import { useState, useMemo } from 'react';
import { LargeTitleWithText } from '@gofranz/common-components';
import {
  Container,
  Grid,
  Card,
  Stack,
  Group,
  Text,
  Badge,
  TextInput,
  Select,
  Box,
  ActionIcon,
  Divider,
  Center,
  Loader,
  Alert,
  Paper,
} from '@mantine/core';
import {
  IconSearch,
  IconX,
  IconCash,
  IconBulb,
  IconEye,
  IconPalette,
  IconSortAscending,
  IconSortDescending,
  IconInfoCircle,
  IconWorld,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { ModelInfo } from '@gofranz/gpt-muse-common';
import { useChatModels } from '../hooks/useChatModels';
import { Footer } from '../components/Layout/Footer';
import { formatPricing } from '../lib/model-utils';
import { getModelBadges } from '../lib/model-ui-utils';

type PricingTier = 'free' | 'budget' | 'standard' | 'premium' | 'enterprise';

interface ModelsByTier {
  free: ModelInfo[];
  budget: ModelInfo[];
  standard: ModelInfo[];
  premium: ModelInfo[];
  enterprise: ModelInfo[];
}

type SortOption = 'name' | 'price_asc' | 'price_desc' | 'provider_count';

export function PricingPage() {
  const { t } = useTranslation();
  const { models, isLoading, error } = useChatModels();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('name');


  const getModelTier = (model: ModelInfo): PricingTier => {
    const totalCost = model.pricing.min_input_cost + model.pricing.min_output_cost;

    if (totalCost === 0) return 'free';
    if (totalCost < 1) return 'budget';
    if (totalCost < 5) return 'standard';
    if (totalCost < 10) return 'premium';
    return 'enterprise';
  };

  const getTierInfo = (tier: PricingTier) => {
    switch (tier) {
      case 'free':
        return {
          title: 'Free Tier',
          description: 'No cost models with best-effort availability',
          color: 'green',
        };
      case 'budget':
        return {
          title: 'Budget Models',
          description: 'Cost-effective models under €1 per 1M tokens',
          color: 'blue',
        };
      case 'standard':
        return {
          title: 'Standard Models',
          description: 'Balanced performance and cost (€1-5 per 1M tokens)',
          color: 'cyan',
        };
      case 'premium':
        return {
          title: 'Premium Models',
          description: 'High-performance models (€5-10 per 1M tokens)',
          color: 'orange',
        };
      case 'enterprise':
        return {
          title: 'Enterprise Models',
          description: 'Top-tier models for demanding workloads (€10+ per 1M tokens)',
          color: 'red',
        };
    }
  };


  const modelsByTier = useMemo(() => {
    const result: ModelsByTier = {
      free: [],
      budget: [],
      standard: [],
      premium: [],
      enterprise: [],
    };

    models.forEach(model => {
      const tier = getModelTier(model);
      result[tier].push(model);
    });

    return result;
  }, [models]);

  const filteredAndSortedModels = useMemo(() => {
    let allModels = selectedTier === 'all'
      ? models
      : modelsByTier[selectedTier as PricingTier] || [];

    // Apply search filter
    if (searchQuery) {
      allModels = allModels.filter(model =>
        model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.family.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    const sortedModels = [...allModels].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price_asc':
          return (a.pricing.min_input_cost + a.pricing.min_output_cost) -
            (b.pricing.min_input_cost + b.pricing.min_output_cost);
        case 'price_desc':
          return (b.pricing.min_input_cost + b.pricing.min_output_cost) -
            (a.pricing.min_input_cost + a.pricing.min_output_cost);
        case 'provider_count':
          return b.provider_count - a.provider_count;
        default:
          return 0;
      }
    });

    return sortedModels;
  }, [models, modelsByTier, selectedTier, searchQuery, sortBy]);

  const renderModelCard = (model: ModelInfo) => (
    <Card key={model.id} p="md" withBorder shadow="sm" h="100%">
      <Stack gap="sm" h="100%">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="lg" fw={600} lineClamp={1}>
              {model.name}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {model.family} • {model.id}
            </Text>
          </Box>

          <Badge
            size="sm"
            color={getTierInfo(getModelTier(model)).color}
            variant="light"
          >
            {getTierInfo(getModelTier(model)).title.replace(' Models', '').replace(' Tier', '')}
          </Badge>
        </Group>

        <Text size="sm" c="dimmed" style={{ flex: 1 }}>
          {model.description}
        </Text>

        <Group gap={4} wrap="nowrap" style={{ minHeight: 28 }}>
          {getModelBadges(model)}
        </Group>

        <Divider />

        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Group gap={4} align="center">
              <IconCash size={14} color="gray" />
              <Text size="sm" fw={500}>
                {formatPricing(model, true)}
              </Text>
            </Group>

            <Text size="xs" c="dimmed">
              {model.provider_count} provider{model.provider_count !== 1 ? 's' : ''}
            </Text>
          </Group>

          {model.pricing.min_input_cost !== model.pricing.max_input_cost ||
            model.pricing.min_output_cost !== model.pricing.max_output_cost ? (
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
              Pricing varies by provider (€{model.pricing.min_input_cost.toFixed(4)} - €{model.pricing.max_input_cost.toFixed(4)} input, €{model.pricing.min_output_cost.toFixed(4)} - €{model.pricing.max_output_cost.toFixed(4)} output)
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );

  const renderTierSection = (tier: PricingTier, models: ModelInfo[]) => {
    if (models.length === 0) return null;

    const tierInfo = getTierInfo(tier);

    return (
      <Box key={tier} mb="xl">
        <Paper p="md" mb="md" withBorder>
          <Group gap="md" align="center">
            <Badge size="lg" color={tierInfo.color} variant="light">
              {tierInfo.title}
            </Badge>
            <Text size="sm" c="dimmed">
              {tierInfo.description}
            </Text>
            <Text size="xs" c="dimmed" ml="auto">
              {models.length} model{models.length !== 1 ? 's' : ''}
            </Text>
          </Group>
        </Paper>

        <Grid>
          {models.map(model => (
            <Grid.Col key={model.id} span={{ base: 12, sm: 6, lg: 4 }}>
              {renderModelCard(model)}
            </Grid.Col>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Container size="xl" py="xl">
        <LargeTitleWithText
          title={t('glob_navigation.pricing')}
          text="Transparent pricing for all AI models. From free models to enterprise-grade solutions, find the perfect fit for your needs."
          mb="xl"
        />

        {/* Filters and Search */}
        <Paper p="md" mb="xl" withBorder>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                placeholder="Search models by name, family, or description..."
                leftSection={<IconSearch size={16} />}
                rightSection={
                  searchQuery ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setSearchQuery('')}
                      size="sm"
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.currentTarget.value)}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Select
                placeholder="All Tiers"
                data={[
                  { value: 'all', label: 'All Tiers' },
                  { value: 'free', label: 'Free Models' },
                  { value: 'budget', label: 'Budget Models' },
                  { value: 'standard', label: 'Standard Models' },
                  { value: 'premium', label: 'Premium Models' },
                  { value: 'enterprise', label: 'Enterprise Models' },
                ]}
                value={selectedTier}
                onChange={(value) => setSelectedTier(value || 'all')}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Select
                placeholder="Sort by"
                leftSection={sortBy.includes('desc') ? <IconSortDescending size={16} /> : <IconSortAscending size={16} />}
                data={[
                  { value: 'name', label: 'Name' },
                  { value: 'price_asc', label: 'Price (Low to High)' },
                  { value: 'price_desc', label: 'Price (High to Low)' },
                  { value: 'provider_count', label: 'Most Providers' },
                ]}
                value={sortBy}
                onChange={(value) => setSortBy(value as SortOption || 'name')}
              />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Loading State */}
        {isLoading && (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">Loading models...</Text>
            </Stack>
          </Center>
        )}

        {/* Error State */}
        {error && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Error Loading Models"
            color="red"
            mb="xl"
          >
            {error.message || 'Failed to load model information. Please try again later.'}
          </Alert>
        )}

        {/* Models Display */}
        {!isLoading && !error && (
          <>
            {/* Summary */}
            <Group justify="space-between" mb="lg">
              <Text size="lg" fw={500}>
                {filteredAndSortedModels.length === models.length
                  ? `All ${models.length} Models`
                  : `${filteredAndSortedModels.length} of ${models.length} Models`}
              </Text>

              {selectedTier === 'all' && searchQuery === '' && (
                <Group gap="lg">
                  <Text size="sm" c="dimmed">
                    Free: {modelsByTier.free.length}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Budget: {modelsByTier.budget.length}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Standard: {modelsByTier.standard.length}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Premium: {modelsByTier.premium.length}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Enterprise: {modelsByTier.enterprise.length}
                  </Text>
                </Group>
              )}
            </Group>

            {/* Models by Tier or Filtered Results */}
            {selectedTier === 'all' && searchQuery === '' ? (
              // Show organized by tiers
              <Stack gap="xl">
                {renderTierSection('free', modelsByTier.free)}
                {renderTierSection('budget', modelsByTier.budget)}
                {renderTierSection('standard', modelsByTier.standard)}
                {renderTierSection('premium', modelsByTier.premium)}
                {renderTierSection('enterprise', modelsByTier.enterprise)}
              </Stack>
            ) : (
              // Show filtered results
              <>
                {filteredAndSortedModels.length === 0 ? (
                  <Center py="xl">
                    <Stack align="center" gap="md">
                      <IconSearch size={48} color="gray" />
                      <Text size="lg" fw={500} c="dimmed">
                        No models found
                      </Text>
                      <Text size="sm" c="dimmed" ta="center">
                        Try adjusting your search or filter criteria
                      </Text>
                    </Stack>
                  </Center>
                ) : (
                  <Grid>
                    {filteredAndSortedModels.map(model => (
                      <Grid.Col key={model.id} span={{ base: 12, sm: 6, lg: 4 }}>
                        {renderModelCard(model)}
                      </Grid.Col>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {/* Legend */}
        <Paper p="md" mt="xl" withBorder>
          <Text size="sm" fw={500} mb="sm">
            Legend:
          </Text>
          <Group gap="xl">
            <Group gap={4}>
              <Text size="sm" c="dimmed" style={{ color: 'var(--mantine-color-green-6)', fontWeight: 'bold' }}>F</Text>
              <Text size="sm" c="dimmed">Free - No cost models</Text>
            </Group>
            <Group gap={4}>
              <IconBulb size={16} color="var(--mantine-color-yellow-6)" />
              <Text size="sm" c="dimmed">Reasoning - Always enabled</Text>
            </Group>
            <Group gap={4}>
              <IconBulb size={16} color="var(--mantine-color-orange-6)" />
              <Text size="sm" c="dimmed">Reasoning* - Can be enabled/disabled</Text>
            </Group>
            <Group gap={4}>
              <IconEye size={16} color="var(--mantine-color-blue-6)" />
              <Text size="sm" c="dimmed">Vision - Supports image inputs</Text>
            </Group>
            <Group gap={4}>
              <Text size="sm" c="dimmed" style={{ color: 'var(--mantine-color-purple-6)', fontWeight: 'bold' }}>T</Text>
              <Text size="sm" c="dimmed">Tools - Function calling support</Text>
            </Group>
            <Group gap={4}>
              <IconWorld size={16} color="var(--mantine-color-teal-6)" />
              <Text size="sm" c="dimmed">Web Search - Can search the web for current information</Text>
            </Group>
            <Group gap={4}>
              <IconPalette size={16} color="var(--mantine-color-pink-6)" />
              <Text size="sm" c="dimmed">Image Generation - Can generate images from text prompts</Text>
            </Group>
          </Group>
          <Text size="xs" c="dimmed" mt="sm">
            All prices shown in EUR per 1 million tokens (input/output). Actual costs may vary by provider and region.
          </Text>
        </Paper>
      </Container>

      <Footer />
    </>
  );
}
